/**
 * Tage in den Kalender legen — am Stück oder einzeln (Konzept Projekt-Tracking,
 * § Schritt 3; Benjamin 02.10.2026).
 *
 * Links die Tage ohne Datum zum Anhaken, darunter der Monat mit dem, was an jedem
 * Tag schon liegt. **Am Stück:** ein Tipp auf den ersten Tag legt alle markierten
 * Tage hintereinander, das Wochenende wird übersprungen (abschaltbar). **Einzeln:**
 * jeder Tipp legt den nächsten markierten Tag, nochmal tippen nimmt ihn heraus.
 * Andere Termine am selben Tag sind ein Hinweis, kein Hindernis. Alles ist erst
 * Vorschau — gerechnet vom Server, damit Bildschirm und Eintrag nicht auseinanderlaufen.
 */
import { useEffect, useMemo, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { MONTHS, WD, addDays, iso, mondayOf } from "../planer/planer-utils";
import { useEintragen } from "../../hooks/useArbeitsplan";
import { useKalender } from "../../hooks/usePlanung";
import { tagName, type Arbeitsplan, type EintragenAntwort } from "../../lib/arbeitsplan";
import "../../styles/planung.css";

type Props = {
  plan: Arbeitsplan;
  /** Vorgewählte Tage; leer = alle Arbeitstage ohne Datum außer Nacharbeit. */
  vorauswahl: string[];
  onClose: () => void;
  onFertig: (text: string) => void;
};

export default function KalenderBlatt({ plan, vorauswahl, onClose, onFertig }: Props) {
  const offen = plan.tage.filter((t) => !t.im_kalender);
  const [auswahl, setAuswahl] = useState<string[]>(() =>
    vorauswahl.length ? vorauswahl : offen.filter((t) => !t.nacharbeit).map((t) => t.id));
  const [modus, setModus] = useState<"am_stueck" | "einzeln">(vorauswahl.length === 1 ? "einzeln" : "am_stueck");
  const [wochenende, setWochenende] = useState(true);
  const [beginn, setBeginn] = useState(plan.vorgabe.beginn || "09:00");
  const [ende, setEnde] = useState(plan.vorgabe.ende || "16:00");
  const [monat, setMonat] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [start, setStart] = useState("");
  const [daten, setDaten] = useState<Record<string, string>>({});
  const [vorschau, setVorschau] = useState<EintragenAntwort | null>(null);
  const rechnen = useEintragen(plan.projekt_id);
  const eintragen = useEintragen(plan.projekt_id);

  // Monatsraster: Montag vor dem Ersten bis Sonntag nach dem Letzten.
  const zellen = useMemo(() => {
    const erster = mondayOf(monat);
    const letzter = new Date(monat.getFullYear(), monat.getMonth() + 1, 0);
    const n = Math.ceil(((letzter.getTime() - erster.getTime()) / 86_400_000 + 1) / 7) * 7;
    return Array.from({ length: n }, (_, i) => addDays(erster, i));
  }, [monat]);
  const { data: termine = [] } = useKalender(iso(zellen[0]), iso(zellen[zellen.length - 1]));
  const belegt = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const t of termine) {
      if (auswahl.includes(t.id)) continue;
      const tag = (t.start_at || "").slice(0, 10);
      (m[tag] ||= []).push(t.projekt_title || t.title || "Termin");
    }
    return m;
  }, [termine, auswahl]);

  const reihenfolge = offen.filter((t) => auswahl.includes(t.id)).map((t) => t.id);
  const einzelnIds = reihenfolge.filter((id) => daten[id]);
  const body = useMemo(() => {
    if (modus === "am_stueck") {
      if (!start || reihenfolge.length === 0) return null;
      return { modus, tag_ids: reihenfolge, start, wochenende_ueberspringen: wochenende, beginn, ende };
    }
    if (einzelnIds.length === 0) return null;
    return { modus, tag_ids: einzelnIds, daten: Object.fromEntries(einzelnIds.map((id) => [id, daten[id]])), wochenende_ueberspringen: wochenende, beginn, ende };
  }, [modus, start, reihenfolge.join(","), einzelnIds.join(","), JSON.stringify(daten), wochenende, beginn, ende]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!body) { setVorschau(null); return; }
    rechnen.mutate({ ...body, vorschau: true }, { onSuccess: setVorschau, onError: () => setVorschau(null) });
  }, [body]); // eslint-disable-line react-hooks/exhaustive-deps

  const imRaster: Record<string, string[]> = {};
  for (const e of vorschau?.eintraege || []) {
    const t = plan.tage.find((x) => x.id === e.tag_id);
    (imRaster[e.datum] ||= []).push(t ? tagName(t) : "Tag");
  }

  const tippen = (d: string) => {
    if (modus === "am_stueck") { setStart(d); return; }
    const schon = Object.entries(daten).find(([, v]) => v === d);
    if (schon) {
      const { [schon[0]]: _weg, ...rest } = daten;
      setDaten(rest);
      return;
    }
    const naechster = reihenfolge.find((id) => !daten[id]);
    if (naechster) setDaten({ ...daten, [naechster]: d });
  };

  const umschalten = (id: string) => {
    setAuswahl((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
    if (daten[id]) { const { [id]: _weg, ...rest } = daten; setDaten(rest); }
  };

  const anzahl = vorschau?.eintraege.length || 0;
  const fertig = () => {
    if (!body) return;
    eintragen.mutate({ ...body, vorschau: false }, {
      onSuccess: (d) => {
        onFertig(d.eintraege.length === 1 ? "1 Tag steht im Kalender." : `${d.eintraege.length} Tage stehen im Kalender.`);
        onClose();
      },
    });
  };
  const heute = iso(new Date());

  return (
    <div className="pl-overlay" onClick={onClose}>
      <div className="pl-drawer apl-blatt" role="dialog" aria-modal="true" aria-labelledby="apl-kal-titel" onClick={(e) => e.stopPropagation()}>
        <header className="apl-blatt-kopf">
          <div style={{ minWidth: 0 }}>
            <button type="button" className="apl-zurueck" onClick={onClose}>‹ Arbeitsplan</button>
            <h2 id="apl-kal-titel">Im Kalender planen</h2>
          </div>
          <button type="button" onClick={onClose} className="pl-closebtn" aria-label="Schließen">×</button>
        </header>

        <div className="apl-blatt-inhalt">
          {offen.length === 0 ? (
            <p className="apl-leise">Alle Tage stehen schon im Kalender.</p>
          ) : (
            <>
              <fieldset className="apl-kal-tage">
                <legend className="apl-cap">Welche Tage</legend>
                {offen.map((t) => (
                  <label key={t.id} className="apl-schalter">
                    <input type="checkbox" checked={auswahl.includes(t.id)} onChange={() => umschalten(t.id)} />
                    <span>
                      <strong>{tagName(t)}</strong> {t.titel !== plan.titel ? t.titel : ""}
                      {modus === "einzeln" && daten[t.id] && <small> → {daten[t.id].slice(8, 10)}.{daten[t.id].slice(5, 7)}.</small>}
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="apl-kal-optionen">
                <div className="apl-arten" role="radiogroup" aria-label="Wie eintragen">
                  <button type="button" role="radio" aria-checked={modus === "am_stueck"} className={"apl-art" + (modus === "am_stueck" ? " is-aktiv" : "")}
                    onClick={() => { setModus("am_stueck"); setDaten({}); }}>Am Stück</button>
                  <button type="button" role="radio" aria-checked={modus === "einzeln"} className={"apl-art" + (modus === "einzeln" ? " is-aktiv" : "")}
                    onClick={() => { setModus("einzeln"); setStart(""); }}>Einzeln</button>
                </div>
                <label className="apl-schalter">
                  <input type="checkbox" checked={wochenende} onChange={(e) => setWochenende(e.target.checked)} />
                  <span>Wochenende überspringen</span>
                </label>
                <span className="apl-kal-zeit">
                  <input type="time" value={beginn} onChange={(e) => setBeginn(e.target.value)} aria-label="Beginn" />
                  <span aria-hidden="true">–</span>
                  <input type="time" value={ende} onChange={(e) => setEnde(e.target.value)} aria-label="Ende" />
                </span>
              </div>
              <p className="apl-leise">
                {modus === "am_stueck"
                  ? "Den ersten Tag antippen — die übrigen folgen hintereinander."
                  : "Jeder Tipp legt den nächsten angehakten Tag; nochmal tippen nimmt ihn heraus."}
              </p>

              <div className="apl-monat">
                <div className="apl-monat-kopf">
                  <button type="button" className="vg-btn" aria-label="Voriger Monat" onClick={() => setMonat(new Date(monat.getFullYear(), monat.getMonth() - 1, 1))}>‹</button>
                  <strong>{MONTHS[monat.getMonth()]} {monat.getFullYear()}</strong>
                  <button type="button" className="vg-btn" aria-label="Nächster Monat" onClick={() => setMonat(new Date(monat.getFullYear(), monat.getMonth() + 1, 1))}>›</button>
                </div>
                <div className="apl-monat-raster" role="grid">
                  {WD.map((w) => <span key={w} className="apl-monat-wt">{w}</span>)}
                  {zellen.map((d) => {
                    const k = iso(d);
                    const fremd = d.getMonth() !== monat.getMonth();
                    const we = d.getDay() === 0 || d.getDay() === 6;
                    const plan_ = imRaster[k] || [];
                    const andere = belegt[k] || [];
                    return (
                      <button
                        key={k}
                        type="button"
                        className={"apl-zelle" + (fremd ? " is-fremd" : "") + (we ? " is-we" : "") + (plan_.length ? " is-plan" : "") + (k === heute ? " is-heute" : "")}
                        onClick={() => tippen(k)}
                        aria-label={`${d.getDate()}. ${MONTHS[d.getMonth()]}${plan_.length ? `, ${plan_.join(", ")}` : ""}${andere.length ? `, schon ${andere.length} Termin${andere.length === 1 ? "" : "e"}` : ""}`}
                      >
                        <span className="apl-zelle-nr">{d.getDate()}</span>
                        {plan_.map((p) => (
                          <span key={p} className="apl-zelle-tag">
                            <span className="apl-lang">{p.replace(" · Nacharbeit", " NA")}</span>
                            <span className="apl-kurz">{p.replace(/^Tag /, "").replace(" · Nacharbeit", "N")}</span>
                          </span>
                        ))}
                        {andere.length > 0 && <span className="apl-zelle-belegt" title={andere.join(", ")}>{andere.length === 1 ? andere[0] : `${andere.length} Termine`}</span>}
                        {andere.length > 0 && <span className="apl-zelle-punkt" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {vorschau && vorschau.hinweise.length > 0 && (
                <ul className="apl-hinweise">
                  {vorschau.hinweise.map((h) => <li key={h}>{h}</li>)}
                </ul>
              )}
              {(rechnen.error || eintragen.error) && <p className="apl-fehler" role="alert">{fehlerText(eintragen.error || rechnen.error)}</p>}

              <div className="apl-blatt-fuss">
                <span className="apl-leise">
                  {anzahl ? vorschau!.eintraege.map((e) => `${e.tag_text}`).join(" · ") : "Noch kein Tag gewählt."}
                </span>
                <button type="button" className="vg-btn vg-btn-primary" disabled={!anzahl || eintragen.isPending || rechnen.isPending} onClick={fertig}>
                  {eintragen.isPending ? "Trägt ein …" : anzahl === 1 ? "1 Tag eintragen" : `${anzahl || ""} Tage eintragen`.trim()}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
